import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import TagReminderService from '../../services/TagReminderService';

import {
  getPairKey,
  getReminderPairKey,
} from './helpers';

const useHorseTaskReminders = ({
  pairs,
  open,
  disabled,
  refreshKey,
}) => {
  const barn = useSelector((state) => state.barn.selectedBarn);
  const pairKeys = [...new Set(pairs.map((pair) => getPairKey(pair.tagId, pair.eventTemplateId)).filter(Boolean))].sort();
  const pairsKey = JSON.stringify(pairKeys);
  const contextKey = JSON.stringify([barn?.id, barn?.tenant, pairsKey, open, refreshKey]);
  const canLoad = open && !!barn?.tenant && pairKeys.length > 0;
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState({
    contextKey: null,
    reminders: [],
    loading: false,
    error: false,
  });
  const currentContext = useRef(contextKey);
  currentContext.current = contextKey;

  useEffect(() => {
    if (!canLoad || disabled) return undefined;
    let active = true;
    const selectedPairs = JSON.parse(pairsKey);
    const tagIds = [...new Set(selectedPairs.map((pair) => Number(pair.split(':')[0])))].sort((left, right) => left - right);
    setResult({
      contextKey,
      reminders: [],
      loading: true,
      error: false,
    });
    TagReminderService.getAllTagReminders({ tagIds }).then((reminders) => {
      if (!active || currentContext.current !== contextKey) return;
      if (!Array.isArray(reminders)) throw new Error('Unexpected reminder response');
      const matches = reminders.filter((reminder) => selectedPairs.includes(getReminderPairKey(reminder)));
      setResult({
        contextKey,
        reminders: [...new Map(matches.map((reminder) => [reminder.id, reminder])).values()],
        loading: false,
        error: false,
      });
    }).catch(() => {
      if (!active || currentContext.current !== contextKey) return;
      setResult({
        contextKey,
        reminders: [],
        loading: false,
        error: true,
      });
    });
    return () => { active = false; };
  }, [contextKey, canLoad, pairsKey, disabled, retry]);

  const reload = useCallback(() => setRetry((value) => value + 1), []);
  const updateReminder = useCallback((reminder) => {
    if (currentContext.current !== contextKey) return;
    setResult((previous) => {
      if (previous.contextKey !== contextKey) return previous;
      const reminders = previous.reminders.filter((item) => Number(item.id) !== Number(reminder.id));
      if (JSON.parse(pairsKey).includes(getReminderPairKey(reminder))) reminders.push(reminder);
      return {
        ...previous,
        reminders,
      };
    });
  }, [contextKey, pairsKey]);
  const removeReminder = useCallback((id) => {
    if (currentContext.current !== contextKey) return;
    setResult((previous) => {
      if (previous.contextKey !== contextKey) return previous;
      return {
        ...previous,
        reminders: previous.reminders.filter((reminder) => Number(reminder.id) !== Number(id)),
      };
    });
  }, [contextKey]);
  const current = result.contextKey === contextKey;
  return {
    contextKey,
    reminders: current && canLoad
      ? result.reminders
      : [],
    loading: canLoad && (!current || result.loading),
    error: canLoad && current && result.error,
    reload,
    updateReminder,
    removeReminder,
  };
};

export default useHorseTaskReminders;
